export const SUPPORTED_LOCALES = ['en', 'es'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

export function isLocale(value: string | undefined): value is Locale {
  return value !== undefined && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Route keys mapped to their localized URL segment.
 *
 * URLs are part of the product: a Spanish visitor lands on `/es/viajes/...`, not
 * on an English path with a Spanish page. Everything that builds a link goes
 * through {@link localizedPath} so the two stay in sync.
 */
export const ROUTE_SEGMENTS = {
  home: { en: '', es: '' },
  trips: { en: 'trips', es: 'viajes' },
  fleet: { en: 'fleet', es: 'flota' },
  about: { en: 'about', es: 'nosotros' },
  contact: { en: 'contact', es: 'contacto' },
  booking: { en: 'booking', es: 'reserva' },
  review: { en: 'review', es: 'resena' },
  reviews: { en: 'reviews', es: 'resenas' },
} as const satisfies Record<string, Record<Locale, string>>;

export type RouteKey = keyof typeof ROUTE_SEGMENTS;

/** The default locale is served from the bare path; others are prefixed. */
export function localePrefix(locale: Locale): string {
  return locale === DEFAULT_LOCALE ? '' : `/${locale}`;
}

export function localizedPath(key: RouteKey, locale: Locale, ...segments: string[]): string {
  const parts = [ROUTE_SEGMENTS[key][locale], ...segments].filter(Boolean);
  const path = `${localePrefix(locale)}/${parts.join('/')}`;
  return path.length > 1 ? path.replace(/\/$/, '') : '/';
}

/** Derives the locale from a URL path. The URL is the single source of truth. */
export function localeFromPath(pathname: string): Locale {
  const first = pathname.replace(/^\//, '').split('/')[0];
  return isLocale(first) ? first : DEFAULT_LOCALE;
}

/** Builds the `hreflang` alternate map for a route across every locale. */
export function alternatesFor(
  key: RouteKey,
  segmentsByLocale: Partial<Record<Locale, string[]>> = {},
): Record<Locale, string> {
  return Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [
      locale,
      localizedPath(key, locale, ...(segmentsByLocale[locale] ?? [])),
    ]),
  ) as Record<Locale, string>;
}
